import { useEffect } from 'react';
import { DataPacket_Kind, Participant, Room } from 'livekit-client';
import { getNameFromMetadata } from 'useCase';
import { reactionsStore } from 'state/store';
import { Reaction, ReactionType } from '../types';
import { useReactionsVisibilityCleanup } from './useReactionsVisibilityCleanup';
import { useReactionsTimers } from './useReactionsTimers';

const REACTION_DATA_TOPIC = 'reaction';

interface ReactionData {
    emojiString: string;
    participantId?: string;
    participantName?: string;
    timestamp?: number;
    nonce?: string;
    sid?: string;
}

const normalizeEmojiToReactionType = (emoji: string): ReactionType | null => {
    if (Object.values(ReactionType).includes(emoji as ReactionType)) {
        return emoji as ReactionType;
    }
    return null;
};

export const useReactionsEvents = (room: Room | undefined) => {
    useReactionsVisibilityCleanup();
    useReactionsTimers();

    useEffect(() => {
        if (!room) {
            return;
        }

        const handleDataReceived = (
            payload: Uint8Array,
            participant?: Participant,
            kind?: DataPacket_Kind,
            topic?: string,
        ) => {
            if (topic !== undefined && topic !== REACTION_DATA_TOPIC) return;
            if (!participant) return;

            try {
                const decoder = new TextDecoder();
                const message = decoder.decode(payload);

                let data: ReactionData;
                try {
                    data = JSON.parse(message);
                } catch (parseError) {
                    console.error('[Reactions] Failed to parse reaction data JSON:', parseError, {
                        message,
                        participantId: participant.identity,
                        topic,
                    });
                    return;
                }

                if (!data.emojiString) {
                    console.warn('[Reactions] Received data without emojiString field:', data, {
                        participantId: participant.identity,
                        topic,
                    });
                    return;
                }

                const reactionType = normalizeEmojiToReactionType(data.emojiString);
                if (!reactionType) {
                    console.warn('[Reactions] Unknown reaction emojiString:', data.emojiString, {
                        receivedData: data,
                        participantId: participant.identity,
                        allReactionTypes: Object.values(ReactionType),
                    });
                    return;
                }

                const reaction: Reaction = {
                    type: reactionType,
                    participantId: participant.identity,
                    participantName: data.participantName || getNameFromMetadata(participant.metadata, ''),
                    timestamp: data.timestamp || Date.now(),
                    id: `${participant.identity}-${data.timestamp || Date.now()}-${data.nonce ?? 'r'}`,
                    colorIdentity: data.sid ?? participant.sid,
                };

                reactionsStore.addReaction(reaction);
                reactionsStore.setParticipantReaction(reaction.participantId, reaction.type, reaction.timestamp);
            } catch (error) {
                console.error('[Reactions] Failed to parse reaction data:', error, {
                    payloadLength: payload.length,
                    participantId: participant?.identity,
                    topic,
                });
            }
        };

        room.on('dataReceived', handleDataReceived);

        return () => {
            room.off('dataReceived', handleDataReceived);
        };
    }, [room]);
};
